/* ═══════════════════════════════════════════════════════════════════════════
   Basket state
   ───────────────────────────────────────────────────────────────────────────
   A reducer rather than a query, because the basket is client-owned until
   checkout: it must survive being offline, must respond on the same frame as
   the tap, and has no server truth to reconcile against yet.

   One rule shapes the whole model: a basket belongs to exactly one price list.
   Marketplaces that allow mixed baskets have to solve multi-vendor splitting,
   multiple delivery fees and partial cancellation — real problems, but not
   ones a customer asked for. Adding from a second list prompts, then replaces.

   "List" rather than "provider" is the important word. Nearly every basket is
   built against HexClean's own list for a category and has no provider at all
   until the order is confirmed and one is assigned. A basket that carried a
   provider id from the first tap would be asking the customer to have chosen
   a shop, which is exactly the question this flow does not ask.
   ═══════════════════════════════════════════════════════════════════════════ */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react'
import { lineKey } from '../lib/lineKey'
import { load, save } from '../lib/storage'
import type { CartLine, CategoryId, ListId, Venue } from '../lib/types'

/** Everything about a basket that is not a line. Passed as one object because
 *  the four fields are only ever correct together — a provider id without the
 *  matching list id, or a `you-come` venue without a provider, is a basket
 *  that cannot be checked out. */
export interface CartTarget {
  listId: ListId
  categoryId: CategoryId
  /** Set only where the customer chose the partner themselves — a car wash
   *  they are driving to. Null everywhere else: we assign, after they order. */
  providerId: string | null
  venue: Venue
}

export interface CartState {
  listId: ListId | null
  categoryId: CategoryId | null
  providerId: string | null
  venue: Venue
  lines: CartLine[]
}

const EMPTY: CartState = {
  listId: null,
  categoryId: null,
  providerId: null,
  venue: 'we-come',
  lines: [],
}

type Action =
  | { type: 'add'; target: CartTarget; line: Omit<CartLine, 'key' | 'listId'> }
  | { type: 'setQty'; key: string; qty: number }
  | { type: 'remove'; key: string }
  | { type: 'restore'; line: CartLine; at: number }
  | { type: 'clear' }
  | { type: 'hydrate'; state: CartState }

function reducer(state: CartState, action: Action): CartState {
  switch (action.type) {
    case 'hydrate':
      return action.state

    case 'add': {
      const key = lineKey(action.line.itemId, action.line.optionIds)
      // Switching list replaces the basket. The confirmation happens in the
      // UI before this dispatches — the reducer is not the place to ask.
      const base = state.listId && state.listId !== action.target.listId ? [] : state.lines
      const at = base.findIndex((l) => l.key === key)

      const lines =
        at >= 0
          ? base.map((l, i) =>
              i === at ? { ...l, qty: l.qty + action.line.qty, notes: action.line.notes ?? l.notes } : l,
            )
          : [...base, { ...action.line, listId: action.target.listId, key }]

      return { ...action.target, lines }
    }

    case 'setQty': {
      if (action.qty <= 0) return reducer(state, { type: 'remove', key: action.key })
      return {
        ...state,
        lines: state.lines.map((l) => (l.key === action.key ? { ...l, qty: action.qty } : l)),
      }
    }

    case 'remove': {
      const lines = state.lines.filter((l) => l.key !== action.key)
      // An empty basket forgets its list, otherwise the next add from a
      // different one would prompt about a basket with nothing in it.
      return lines.length === 0 ? EMPTY : { ...state, lines }
    }

    case 'restore': {
      // Undo puts the line back where it was, not at the end. A row that
      // reappears in a different position reads as a second, different row.
      const lines = [...state.lines]
      lines.splice(Math.min(action.at, lines.length), 0, action.line)
      return { ...state, lines }
    }

    case 'clear':
      return EMPTY

    default:
      return state
  }
}

interface CartApi extends CartState {
  add: (target: CartTarget, line: Omit<CartLine, 'key' | 'listId'>) => void
  setQty: (key: string, qty: number) => void
  remove: (key: string) => void
  restore: (line: CartLine, at: number) => void
  clear: () => void
  /** Quantity currently in the basket for an item, summed across option sets.
   *  Drives the "3" badge on a menu row. */
  qtyOf: (itemId: string) => number
  count: number
  isEmpty: boolean
}

const CartContext = createContext<CartApi | null>(null)

const KEY = 'cart'

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, EMPTY, () => load<CartState>(KEY, EMPTY))

  /* Persist on every change. Synchronous and unthrottled on purpose: the write
     is sub-millisecond for a basket-sized object, and a debounce here is how a
     basket is lost when the tab is closed a moment after the last tap. */
  useEffect(() => {
    save(KEY, state)
  }, [state])

  const add = useCallback(
    (target: CartTarget, line: Omit<CartLine, 'key' | 'listId'>) =>
      dispatch({ type: 'add', target, line }),
    [],
  )
  const setQty = useCallback((key: string, qty: number) => dispatch({ type: 'setQty', key, qty }), [])
  const remove = useCallback((key: string) => dispatch({ type: 'remove', key }), [])
  const restore = useCallback((line: CartLine, at: number) => dispatch({ type: 'restore', line, at }), [])
  const clear = useCallback(() => dispatch({ type: 'clear' }), [])

  const value = useMemo<CartApi>(() => {
    const count = state.lines.reduce((n, l) => n + l.qty, 0)
    return {
      ...state,
      add,
      setQty,
      remove,
      restore,
      clear,
      qtyOf: (itemId) =>
        state.lines.reduce((n, l) => (l.itemId === itemId ? n + l.qty : n), 0),
      count,
      isEmpty: state.lines.length === 0,
    }
  }, [state, add, setQty, remove, restore, clear])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartApi {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>')
  return ctx
}
